import { templateContent } from "./template.js";
import {
  PLAYED,
  UNPLAYED,
  clockTime,
  keyTime,
  playedCells,
  pointerTime,
  sliderValues,
} from "./video-scrub.js";

// The video player on the About this site page: a round play button over the video, and a rail
// under it with the time, a scrubber made of the field's circle glyphs, and the length. Without
// the script, you get the browser's own controls.

// One cell per this many em of the scrubber's font (14px at its default 13px), within these
// counts, but never more cells than the glyphs fit (DejaVu Sans Mono's advance is 1233 of 2048
// units). A larger default font size gives fewer cells, not an overflow.
const CELL_EM = 14 / 13;
const GLYPH_EM = 1233 / 2048;
const MIN_CELLS = 12;
const MAX_CELLS = 48;
// While the video plays, the button shows under a pointer and for this long after it leaves, so
// it doesn't sit over the picture.
const HOVER_LINGER_MS = 100;
// On a touch screen, a tap on the video shows it for this long, like other video players do.
const TAP_SHOW_MS = 2500;
// A touch on the scrubber seeks once it moves this far sideways, or on a tap. A page scroll can
// start on the scrubber (`touch-action: pan-y`), and that shouldn't seek.
const TOUCH_SLOP_PX = 8;

export class JrsmVideo extends HTMLElement {
  private video: HTMLVideoElement | null = null;
  private button: HTMLButtonElement | null = null;
  private scrub: HTMLElement | null = null;
  private now: HTMLElement | null = null;
  private length: HTMLElement | null = null;
  private cells: HTMLSpanElement[] = [];
  // The video's title, for the controls' names ("Play the preloader").
  private label = "the video";
  // Whether the face paused the video, so it can play again when it comes back out.
  private pausedByFace = false;
  // While its picture dissolves under the face, the player takes no input.
  private locked = false;
  // For showing the button (`data-hover`): the timer that hides it, and the last pointer's type.
  private hideTimer = 0;
  private pointerType = "";
  // Ends this connection's listeners and observers.
  private connection: AbortController | null = null;

  /** How far below the top of the viewport a keyboard focus puts the video's top, in CSS
      pixels, so none of the stopped portrait's dots reach it. The page sets it (home.ts), and
      it's 0 where nothing covers the page. */
  focusClearance = 0;

  // How the face talks to the player (`attributeChangedCallback`). text-effects.ts writes
  // `--dissolve` into the player's style as the video goes under the face, and with motion on,
  // the halftone layer sets `data-halftone` while the picture dissolves. They're marks on the
  // player, so the effects don't need to know anything about players.
  static readonly observedAttributes = ["style", "data-halftone"];

  attributeChangedCallback(): void {
    if (this.isConnected) this.dissolveChanged();
  }

  connectedCallback(): void {
    this.build();
    const { video, button, scrub } = this;
    if (!video || !button || !scrub) return;
    this.connection = new AbortController();
    const { signal } = this.connection;

    this.addEventListener("pointerdown", (event) => (this.pointerType = event.pointerType), {
      signal,
    });
    this.addEventListener("focusin", (event) => this.clearOfFace(event.target), { signal });
    this.addEventListener(
      "pointerenter",
      (event) => {
        if (event.pointerType !== "touch") this.show();
      },
      { signal },
    );
    this.addEventListener(
      "pointerleave",
      (event) => {
        if (event.pointerType !== "touch") this.show(HOVER_LINGER_MS);
      },
      { signal },
    );
    button.addEventListener(
      "click",
      () => {
        this.toggle();
        // On a touch screen, the button stays for a bit after a tap, then hides.
        if (this.pointerType === "touch") this.show(TAP_SHOW_MS);
      },
      { signal },
    );
    // A click on the video plays or pauses it; a tap shows or hides its button.
    video.addEventListener(
      "click",
      () => {
        if (this.pointerType !== "touch") this.toggle();
        else if (this.hasAttribute("data-hover")) this.hide();
        else this.show(TAP_SHOW_MS);
      },
      { signal },
    );
    for (const type of ["play", "pause", "ended", "timeupdate", "durationchange", "seeked"])
      video.addEventListener(type, () => this.render(type === "timeupdate"), { signal });
    // When no source loads, only the last `<source>` fires an error (it doesn't bubble, so the
    // listener captures it), and `play()` waits for a new source. A media error after the start
    // (say the network drops) fires on the video itself. Either way the video stops, so its
    // button offers play again instead of pause for a video that isn't playing. A stall keeps
    // the pause button, like other players do.
    video.addEventListener(
      "error",
      () => {
        if (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) video.pause();
      },
      { capture: true, signal },
    );
    // One video plays at a time.
    video.addEventListener(
      "play",
      () => {
        for (const other of document.querySelectorAll("jrsm-video"))
          if (other !== this) other.pause();
      },
      { signal },
    );
    scrub.addEventListener(
      "keydown",
      (event) => {
        if (this.locked) return;
        const time = keyTime(event.key, video.currentTime, this.duration());
        if (time === null) return;
        event.preventDefault();
        this.seek(time);
      },
      { signal },
    );
    // Where a touch started, until it seeks.
    let touchX: number | null = null;
    scrub.addEventListener(
      "pointerdown",
      (event) => {
        if (this.locked) return;
        scrub.setPointerCapture(event.pointerId);
        if (event.pointerType === "touch") touchX = event.clientX;
        else this.seekTo(event.clientX);
      },
      { signal },
    );
    scrub.addEventListener(
      "pointermove",
      (event) => {
        if (this.locked || !scrub.hasPointerCapture(event.pointerId)) return;
        if (touchX !== null) {
          if (Math.abs(event.clientX - touchX) < TOUCH_SLOP_PX) return;
          touchX = null;
        }
        this.seekTo(event.clientX);
      },
      { signal },
    );
    scrub.addEventListener(
      "pointerup",
      (event) => {
        if (!this.locked && touchX !== null) this.seekTo(event.clientX);
        touchX = null;
      },
      { signal },
    );
    // The browser decided the touch was a page scroll.
    scrub.addEventListener("pointercancel", () => (touchX = null), { signal });
    const sized = new ResizeObserver(() => this.layoutCells());
    sized.observe(scrub);
    this.layoutCells();
    signal.addEventListener("abort", () => sized.disconnect(), { once: true });
    // While it was out of the page, the player heard nothing (its video was paused), so it reads
    // the video and the face's marks again.
    this.render();
    this.dissolveChanged();
  }

  // Out of the page, the player stops everything: its listeners, observers, timer, and the
  // video. Its markup stays for a reconnection.
  disconnectedCallback(): void {
    this.connection?.abort();
    this.connection = null;
    this.hide();
    this.pause();
  }

  // The player's markup, built once: the video in a frame with its button, and the rail.
  private build(): void {
    if (this.video) return;
    const video = this.querySelector("video");
    if (!video) throw new Error("jrsm-video: video missing");
    this.video = video;
    this.label = video.getAttribute("aria-label") ?? this.label;
    video.controls = false;
    // The controls change as the video plays, so they aren't text for the text effects to wrap
    // or watch.
    this.dataset.fx = "none";
    // The video and its button share a frame, so the button centers on the video. The frame is
    // in the markup because in Chromium, a video that a script moves after the parser starts
    // its source selection only ever tries its first source.
    const frame = video.parentElement;
    if (!frame?.classList.contains("video-frame")) throw new Error("jrsm-video: frame missing");
    const controls = templateContent("video-controls");
    const button = controls.querySelector(".video-play");
    const rail = controls.querySelector(".video-rail");
    const scrub = controls.querySelector(".video-scrub");
    const now = controls.querySelector(".video-now");
    const length = controls.querySelector(".video-length");
    if (
      !(button instanceof HTMLButtonElement) ||
      !(rail instanceof HTMLElement) ||
      !(scrub instanceof HTMLElement) ||
      !(now instanceof HTMLElement) ||
      !(length instanceof HTMLElement)
    )
      throw new Error("jrsm-video: controls missing");
    frame.append(button);
    this.append(rail);
    scrub.setAttribute("aria-label", `Seek ${this.label}`);
    // The video's caption describes its controls too. Chromium exposes a video without native
    // controls as a generic element, so the video's own description never reaches a screen
    // reader.
    const described = video.getAttribute("aria-describedby");
    if (described)
      for (const control of [button, scrub]) control.setAttribute("aria-describedby", described);
    this.button = button;
    this.scrub = scrub;
    this.now = now;
    this.length = length;
  }

  /** Pauses for another video. Your choice beats the face's hold, so the video doesn't start
      again on its own when it comes back out. */
  pause(): void {
    this.pausedByFace = false;
    this.toggleAttribute("data-held", false);
    this.video?.pause();
  }

  // Shows the button for `ms` and then hides it, or without `ms`, until the next change.
  private show(ms?: number): void {
    clearTimeout(this.hideTimer);
    this.toggleAttribute("data-hover", true);
    if (ms !== undefined) this.hideTimer = window.setTimeout(() => this.hide(), ms);
  }

  private hide(): void {
    clearTimeout(this.hideTimer);
    this.toggleAttribute("data-hover", false);
  }

  // Before the video loads (`preload="none"`), the page gives its length.
  private duration(): number {
    const loaded = this.video?.duration ?? Number.NaN;
    return Number.isFinite(loaded) ? loaded : Number(this.dataset.duration ?? 0);
  }

  // A held video is also locked (`dissolveChanged()`), so a click never runs into a hold.
  private toggle(): void {
    const video = this.video;
    if (!video || this.locked) return;
    if (video.paused) this.play();
    else video.pause();
  }

  private play(): void {
    const video = this.video;
    if (!video) return;
    // After a failed load, the element waits for a new source, so pressing play again retries
    // the sources (the network may be back).
    if (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) video.load();
    video.play().catch((error: unknown) => {
      // A pause or a seek before playback starts interrupts `play()`. That's not a failure.
      if (!(error instanceof DOMException && error.name === "AbortError"))
        console.warn("Video: did not play", error);
    });
  }

  // Under the face, a playing video pauses, so the dissolve (halftone-layer.ts) gets the frame
  // where it stopped. It plays again when it comes back out.
  private dissolveChanged(): void {
    const video = this.video;
    if (!video) return;
    const fade = Number.parseFloat(this.style.getPropertyValue("--dissolve")) || 0;
    const under = fade > 0 || this.hasAttribute("data-halftone");
    this.lock(under);
    if (under && !video.paused) {
      this.pausedByFace = true;
      this.toggleAttribute("data-held", true);
      video.pause();
    }
    if (!under && this.pausedByFace) {
      this.pausedByFace = false;
      this.toggleAttribute("data-held", false);
      this.play();
    }
  }

  // Once the face's dissolve touches the picture, the player takes no input until the picture
  // is whole again. The controls stay in the tab order (`aria-disabled`, not `disabled`), so a
  // keyboard user can still reach them, and focusing one scrolls the video clear
  // (`clearOfFace()`).
  private lock(on: boolean): void {
    const { button, scrub } = this;
    if (on === this.locked || !button || !scrub) return;
    this.locked = on;
    this.toggleAttribute("data-locked", on);
    for (const control of [button, scrub])
      if (on) control.setAttribute("aria-disabled", "true");
      else control.removeAttribute("aria-disabled");
  }

  // The browser's focus scroll stops at the control, which sits below the video's top, so it can
  // leave the video under the face and lock the very control that has focus. So a keyboard
  // focus first scrolls the video's top clear of the dots, and the browser has no scrolling left
  // to do. A click focuses without scrolling.
  private clearOfFace(target: EventTarget | null): void {
    const { video } = this;
    const clear = this.focusClearance;
    // Nothing covers the page (the portrait scrolls with it; see home.ts).
    if (clear <= 0 || !video) return;
    if (!(target instanceof HTMLElement) || !target.matches(":focus-visible")) return;
    const top = video.getBoundingClientRect().top;
    const control = target.getBoundingClientRect();
    if (top >= clear && control.bottom <= window.innerHeight) return;
    window.scrollBy({ top: top - clear, behavior: "instant" });
  }

  private seek(time: number): void {
    if (this.video) this.video.currentTime = time;
    this.render();
  }

  private seekTo(x: number): void {
    const box = this.scrub?.getBoundingClientRect();
    if (box) this.seek(pointerTime(x, box.left, box.width, this.duration()));
  }

  private layoutCells(): void {
    const scrub = this.scrub;
    if (!scrub) return;
    const font = Number.parseFloat(getComputedStyle(scrub).fontSize);
    const width = scrub.clientWidth;
    const count = Math.min(
      MAX_CELLS,
      Math.floor(width / (font * GLYPH_EM)),
      Math.max(MIN_CELLS, Math.floor(width / (font * CELL_EM))),
    );
    if (count === this.cells.length) return;
    // The glyphs are a picture of the time, and the slider's values say it in words. Exposed as
    // text, the cells would be read as the slider's value.
    this.cells = Array.from({ length: count }, () => {
      const cell = document.createElement("span");
      cell.setAttribute("aria-hidden", "true");
      return cell;
    });
    scrub.replaceChildren(...this.cells);
    this.render();
  }

  // `playing` means a time update while the video plays. Then the slider keeps its values while
  // it has focus, because a screen reader reads out every change to a focused slider. A seek,
  // a pause, or the end updates them.
  private render(playing = false): void {
    const { video, button, scrub, now, length } = this;
    if (!video || !button || !scrub || !now || !length) return;
    const duration = this.duration();
    const time = video.currentTime;
    const state = video.ended ? "ended" : video.paused ? "paused" : "playing";
    this.dataset.state = state;
    button.setAttribute("aria-label", `${state === "playing" ? "Pause" : "Play"} ${this.label}`);
    const played = playedCells(time, duration, this.cells.length);
    // Only the cells that change get written, since this runs on every time update.
    this.cells.forEach((cell, index) => {
      const on = index < played;
      if (cell.textContent && cell.classList.contains("played") === on) return;
      cell.textContent = on ? PLAYED : UNPLAYED;
      cell.classList.toggle("played", on);
    });
    if (!playing || document.activeElement !== scrub) {
      const values = sliderValues(time, duration);
      scrub.setAttribute("aria-valuemax", String(values.max));
      scrub.setAttribute("aria-valuenow", String(values.now));
      scrub.setAttribute("aria-valuetext", values.text);
    }
    now.textContent = clockTime(time);
    length.textContent = clockTime(duration);
  }
}

customElements.define("jrsm-video", JrsmVideo);
